export interface ErrorResponse {
  statusCode: number;
  errorCode: string;
  message: string;
  tracer?: string;
  detail?: string;
  errorTypeDescription?: string;
}

export class ApiError extends Error {
  payload: ErrorResponse;

  constructor(message: string, payload: ErrorResponse) {
    super(message);

    this.name = 'ApiError';
    this.payload = payload;

    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

export const handleResponse = async <T = any>(
  res: Response,
  getApiErrorMessage?: (error: ErrorResponse) => string,
): Promise<T> => {
  const body = await res.text();

  if (res.ok) {
    // success response
    const parsedBody = body.length ? JSON.parse(body) : body;

    return parsedBody;
  }

  // error response
  let payload: ErrorResponse | null = null;

  try {
    // attempt to parse json error
    payload = JSON.parse(body);
  } catch (error) {}

  if (payload) {
    throw new ApiError(getApiErrorMessage?.(payload) ?? payload.message, payload);
  } else {
    throw Error(body);
  }
};

export type PaginatedResponse<T> = {
  items: T;
  totalPages: number;
  totalRecords: number;
};

export const handlePaginatedResponse = async <T = any>(
  res: Response,
  getApiErrorMessage?: (error: ErrorResponse) => string,
): Promise<PaginatedResponse<T>> =>
  handleResponse<T>(res, getApiErrorMessage).then((items) => {
    const totalPages = Number(res.headers.get('Page-Count')) || 0;
    const totalRecords = Number(res.headers.get('Total-Record-Count')) || 0;

    return {
      items,
      totalPages,
      totalRecords,
    };
  });

export const getAprilApiBaseUri = (apiBaseUri: string): string => {
  let aprilApi = '';

  switch (apiBaseUri) {
    case 'https://api.dev.limep.net':
      aprilApi = 'https://api.dev.au.meetapril.dev';
      break;
    case 'https://api.tst.limep.net':
      aprilApi = 'https://api.tst.au.meetapril.dev';
      break;
    case 'https://api.sandbox.limepay.com.au':
      aprilApi = 'https://api.sandbox.au.meetapril.io';
      break;
    default:
      aprilApi = 'https://api.au.meetapril.io';
  }
  return aprilApi;
};

export function toQueryString(params: Record<string, any>, arrays?: string[]): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      if (arrays && arrays.includes(key) && typeof value === 'string') {
        const items = value.split(',');
        items.forEach((item) => searchParams.append(key, item));
      } else {
        searchParams.append(key, value.toString());
      }
    }
  }

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}
